import el from "@cypherpotato/el";
import page from "page";
import { logoutCurrentAccountAndLoginNext } from "../../account-switching";
import { api } from "../../api";
import { ClearAllCache } from "../../cache";
import { PlanBadge } from "../../components/badge";
import { Button, ButtonRow, DangerButton } from "../../components/button";
import { CommandBar } from "../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../components/dashboard";
import { Dropdown, DropdownItem } from "../../components/dropdown";
import { Editor, EditorDescription, EditorRow, EditorTitle } from "../../components/editor";
import { FormControl, NumberInput, Select, TextInput } from "../../components/form";
import { Icon } from "../../components/icon";
import { Content, Row, Section, SmallContainer } from "../../components/layout";
import { ButtonLink, ExternalLink } from "../../components/link";
import { SpecializedControl } from "../../components/specializedObject";
import { TabControl } from "../../components/tabcontrol";
import { DefaultRow, Table, TableActionCell, TableBody, TableCell, TableHeader, TableHeaderActionCell, TableHeaderCell, TableRow } from "../../components/table";
import { BlockCodeWrapper, CheckWrapper, Heading, MutedText, Paragraph } from "../../components/text";
import { Information, Warning } from "../../components/warn";
import { getSession, putSession, removeCurrentSavedAccount } from "../../session";
import { displayAlert, displayFormControl } from "../../ui/alert";
import { copyToClipboard } from "../../ui/clipboard";
import { showFlyout } from "../../ui/flyout";
import { formatDate, formatDateTime } from "../../ui/format";
import { runOperation } from "../../ui/operation";
import { ellipsis, valueFrom } from "../../util";
import { Card, CardContent, CardHeader } from "../../components/card";
import { showAccountPlanDialog } from "./plan-dialog";
import { showTwoFactorDialog } from "./security-dialog";


export default function (ctx) {

    const session = getSession(true);
    const apiKeys = el.state.create(null);
    const applications = el.state.create(null);
    const subscription = el.state.create(null);

    function normalizeNotificationSettings(parameters = {}) {
        const notifications = parameters.notifications ?? {};
        return {
            emails: [],
            weeklyTestingSummary: notifications.weeklyTestingSummary ?? false,
            repeatedTestFailures: notifications.repeatedTestFailures ?? false,
            lowBalance: {
                enabled: notifications.lowBalance?.enabled ?? false,
                threshold: notifications.lowBalance?.threshold ?? 20
            },
            deprecatedModels: {
                enabled: notifications.deprecatedModels?.enabled ?? true
            }
        };
    }

    function getAccountPayload(overrides = {}) {
        const name = overrides.name ?? valueFrom("accountName") ?? session.data.name;
        const params = session.data.parameters ?? {};
        return {
            name,
            parameters: {
                ...params,
                allowConversationsLogging: params.allowConversationsLogging ?? true,
                allowRagLogging: params.allowRagLogging ?? true,
                automaticJsonHealing: params.automaticJsonHealing ?? true,
                allowSemanticDataCollection: params.allowSemanticDataCollection ?? false,
                zeroDataRetentionOnly: params.zeroDataRetentionOnly ?? false,
                allowSpecialProviders: params.allowSpecialProviders ?? false,
                notifications: normalizeNotificationSettings(params),
                ...overrides.parameters
            }
        };
    }

    async function updateAccount(overrides = {}) {
        const payload = getAccountPayload(overrides);
        await api.putJson("/api/v1/accounts", payload);
        session.data.name = payload.name;
        session.data.parameters = payload.parameters;
        putSession(session);
    }

    function getApiKeys() {
        runOperation("Getting things ready...", async () => {
            const result = await api.getJson("/api/v1/accounts/api-key");
            apiKeys.value = result.data;
        });
    }

    function getApplications() {
        runOperation("Loading applications...", async () => {
            const result = await api.getJson("/api/v1/accounts/applications");
            applications.value = result.data;
        });
    }

    function getSubscription() {
        runOperation("Loading subscription...", async () => {
            const result = await api.getJson("/api/v1/subscriptions");
            subscription.value = result.data;
        });
    }

    function displayPlanPicker() {
        showAccountPlanDialog(subscription.value, plan =>
            runOperation("Updating subscription...", async () => {
                const result = await api.postJson("/api/v1/subscriptions/plan", { plan });
                if (result.data.paymentLocation) {
                    window.open(result.data.paymentLocation, "_blank", "noopener");
                }
                getSubscription();
            })
        );
    }

    getApiKeys();
    getApplications();
    getSubscription();

    return Dashboard(
        DashboardTitle(
            "My account",
            "Control your usage, add balance and configure your account here."
        ),
        Section(
            TabControl([
                {
                    title: "Account information",
                    contents: el.fragment(
                        el.state.listening([subscription], () =>
                            Section(
                                Editor(
                                    {
                                        id: "accountPlan",
                                        value: subscription.value?.plan ?? window.authenticationDetails?.plan,
                                        readonly: true,
                                        hoverState: false,
                                        renderValue(value) {
                                            return value && PlanBadge(value) || MutedText("No plan information");
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Account plan"),
                                        Paragraph("Upgrade, downgrade or manage your account plan here."),
                                        subscription.value?.pendingPaymentLocation && Warning(
                                            Paragraph(
                                                "A plan payment is pending. The plan is activated as soon as the payment is confirmed. ",
                                                ExternalLink("Complete the payment", subscription.value.pendingPaymentLocation)
                                            )
                                        ),
                                        subscription.value?.isCancellationPending && Information(
                                            Paragraph(`Your subscription ends on ${formatDate(subscription.value.cycleEnd)}. You keep the ${subscription.value.plan} plan until then.`)
                                        ),
                                        subscription.value?.pendingPlan && Information(
                                            Paragraph(`Your plan changes to ${subscription.value.pendingPlan} on ${formatDate(subscription.value.cycleEnd)}.`)
                                        )
                                    )
                                ),
                                subscription.value && subscription.value.plan !== "Custom" && EditorRow(
                                    ButtonRow(
                                        subscription.value.plan === "Free" && ButtonLink("Subscribe to plan", {
                                            onClick() {
                                                displayPlanPicker();
                                            }
                                        }),
                                        subscription.value.plan !== "Free" && subscription.value.isCancellationPending && ButtonLink("Resubscribe", {
                                            onClick() {
                                                runOperation("Updating subscription...", async () => {
                                                    await api.postJson("/api/v1/subscriptions/resubscribe", {});
                                                    getSubscription();
                                                });
                                            }
                                        }),
                                        subscription.value.plan !== "Free" && !subscription.value.isCancellationPending && ButtonLink("Cancel subscription", {
                                            onClick() {
                                                displayAlert({
                                                    title: "Cancel subscription",
                                                    message: `Your ${subscription.value.plan} plan stays active until ${formatDate(subscription.value.cycleEnd)}. After that, the account returns to the Free plan and its limits. You can resubscribe at any time before the renewal.`,
                                                    type: "alert-warning",
                                                    buttons: [
                                                        {
                                                            label: "Keep my plan"
                                                        },
                                                        {
                                                            label: "Cancel subscription",
                                                            onClick(event) {
                                                                event.target.closest("dialog").dismiss();
                                                                runOperation("Cancelling subscription...", async () => {
                                                                    await api.postJson("/api/v1/subscriptions/cancel", {});
                                                                    getSubscription();
                                                                });
                                                            }
                                                        }
                                                    ]
                                                });
                                            }
                                        }),
                                        subscription.value.plan !== "Free" && !subscription.value.isCancellationPending && ButtonLink("Change plan", {
                                            onClick() {
                                                displayPlanPicker();
                                            }
                                        }),
                                        subscription.value.plan !== "Free" && subscription.value.supportsSavedPaymentMethods && ButtonLink("Payment methods", {
                                            onClick() {
                                                runOperation("Opening payment methods...", async () => {
                                                    const result = await api.postJson("/api/v1/subscriptions/payment-methods/portal", {});
                                                    window.open(result.data.url, "_blank", "noopener");
                                                });
                                            }
                                        })
                                    )
                                )
                            )
                        ),
                        Section(
                            Editor(
                                {
                                    id: "accountName",
                                    value: session.data.name,
                                    renderValue(value) {
                                        return Button(value || MutedText("None"));
                                    },
                                    manipulateValue(value, callback) {
                                        displayFormControl({
                                            title: this.editorTitle,
                                            message: this.editorParagraphs,
                                            formControlItem: TextInput(null, "Account name", { value }),
                                            onConfirm(newName) {
                                                runOperation("Updating account...", async () => {
                                                    await updateAccount({ name: newName });
                                                    callback(newName);
                                                });
                                            },
                                            validate: [
                                                [x => x.length > 3, "The account name must be at least 3 characters long."]
                                            ]
                                        })
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Account name"),
                                    Paragraph("Edit your account name, which will be visible on the platform.")
                                )
                            ),
                            Editor(
                                {
                                    id: "accountName",
                                    value: session.data.email,
                                    readonly: true,
                                    renderValue(value) {
                                        return Button(value || MutedText("None"));
                                    },
                                    manipulateValue(value, callback) {
                                        displayFormControl({
                                            title: this.editorTitle,
                                            message: this.editorParagraphs,
                                            formControlItem: TextInput(null, "Account name", { value }),
                                            onConfirm: callback,
                                            validate: [
                                                [x => x.length > 3, "The account name must be at least 3 characters long."]
                                            ]
                                        })
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Account e-mail"),
                                    Paragraph("To change your account email, you will be need to confirm it later.")
                                )
                            )
                        ),
                        Section(
                            Editor(
                                {
                                    id: "automaticJsonHealing",
                                    value: session.data.parameters?.automaticJsonHealing ?? true,
                                    renderValue(value) {
                                        return CheckWrapper(value, "enabled", "disabled");
                                    },
                                    manipulateValue(value, callback) {
                                        const newValue = !value;
                                        runOperation("Updating account...", async () => {
                                            await updateAccount({ parameters: { automaticJsonHealing: newValue } });
                                            callback(newValue);
                                        });
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Automatic JSON healing"),
                                    Paragraph("Automatically attempt to fix malformed JSON responses from AI models.")
                                )
                            )
                        ),
                        Section(
                            Editor(
                                {
                                    id: "accountPfp",
                                    value: true,
                                    renderValue(value) {
                                        return Button("Go to Gravatar", Icon("arrow-right-line"))
                                    },
                                    manipulateValue(value, callback) {
                                        window.open("https://gravatar.com/", "_blank");
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Profile picture"),
                                    Paragraph("Your profile picture is provided by ", ExternalLink("Gravatar", "https://gravatar.com/"), "."),
                                    Paragraph(
                                        "To change your profile picture, sign in or create a free account on Gravatar and change your profile picture there. " +
                                        "It will be updated here and all other services that use Gravatar will receive the new photo."
                                    )
                                )
                            )
                        ),
                        Section(
                            Editor(
                                {
                                    id: "accountLogout",
                                    value: true,
                                    renderValue(value) {
                                        return Button("Logout", {
                                            style: {
                                                color: "var(--warn)"
                                            }
                                        })
                                    },
                                    manipulateValue(value, callback) {
                                        runOperation("Logging out...", async () => {
                                            try {
                                                const loggedNextAccount = await logoutCurrentAccountAndLoginNext();
                                                window.location.assign(loggedNextAccount ? "/dashboard" : "/");
                                            } catch {
                                                window.location.assign("/");
                                            }
                                        });
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Logout"),
                                    Paragraph("To log out of your account, click the button next to it.")
                                )
                            )
                        )
                    )
                },
                {
                    title: "Security",
                    contents: el.fragment(
                        Heading(
                            "Security",
                            Paragraph("Protect console sign-in with an authenticator app, or replace your login key.")
                        ),
                        Section(
                            Editor(
                                {
                                    id: "twoFactor",
                                    value: session.data.parameters?.security?.totpKeys?.length || 0,
                                    renderValue(value) {
                                        return value > 0
                                            ? Button(`${value} authenticator${value === 1 ? "" : "s"}`)
                                            : MutedText("Disabled");
                                    },
                                    manipulateValue(value, callback) {
                                        showTwoFactorDialog(keys => {
                                            session.data.parameters = {
                                                ...(session.data.parameters || {}),
                                                security: { totpKeys: keys }
                                            };
                                            putSession(session);
                                            callback(keys.length);
                                        });
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Two-factor authentication"),
                                    Paragraph("Add one or more authenticator apps. The QR code and secret are shown only while you confirm the six-digit code."),
                                    Paragraph("Console sign-in and switching to this account ask for a code when at least one authenticator is confirmed. API keys are not affected.")
                                )
                            ),
                            Editor(
                                {
                                    id: "resetLoginKey",
                                    value: null,
                                    renderValue() {
                                        return Button("Reset login key");
                                    },
                                    manipulateValue(_value, callback) {
                                        displayAlert({
                                            title: "Reset login key",
                                            type: "alert-warning",
                                            message: el.fragment(
                                                Paragraph("This creates a new login key and e-mails it to you. The current key stops working."),
                                                Paragraph("You will be signed out of the console on every device. This browser's saved login and cached account data are cleared."),
                                                Paragraph("API keys are not revoked. Two-factor authentication stays enabled.")
                                            ),
                                            buttons: [
                                                { label: "Keep current key" },
                                                {
                                                    label: "Reset login key",
                                                    onClick(event) {
                                                        event.target.closest("dialog").dismiss();
                                                        runOperation("Resetting login key...", async () => {
                                                            const result = await api.postJson("/api/v1/accounts/login-key/reset", {});
                                                            if (result?.error) return;
                                                            ClearAllCache();
                                                            removeCurrentSavedAccount();
                                                            sessionStorage.clear();
                                                            callback(null);
                                                            window.location.assign("/login?notice=login-key-reset");
                                                        });
                                                    }
                                                }
                                            ]
                                        });
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Login key"),
                                    Paragraph("Replace the login key sent when the account was created. Confirm before continuing, because this signs you out.")
                                )
                            )
                        )
                    )
                },
                {
                    title: "Privacy",
                    contents: el.fragment(
                        Heading(
                            "Privacy",
                            Paragraph("Control which data AIVAX stores about your usage and how your requests are routed.")
                        ),
                        Section(
                            Editor(
                                {
                                    id: "allowConversationsLogging",
                                    value: session.data.parameters?.allowConversationsLogging ?? true,
                                    renderValue(value) {
                                        return CheckWrapper(value, "enabled", "disabled");
                                    },
                                    manipulateValue(value, callback) {
                                        const newValue = !value;
                                        runOperation("Updating account...", async () => {
                                            await updateAccount({ parameters: { allowConversationsLogging: newValue } });
                                            callback(newValue);
                                        });
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Conversations logging"),
                                    Paragraph("Allow AIVAX to log conversation contents for debugging and analytics purposes.")
                                )
                            ),
                            Editor(
                                {
                                    id: "allowRagLogging",
                                    value: session.data.parameters?.allowRagLogging ?? true,
                                    renderValue(value) {
                                        return CheckWrapper(value, "enabled", "disabled");
                                    },
                                    manipulateValue(value, callback) {
                                        const newValue = !value;
                                        runOperation("Updating account...", async () => {
                                            await updateAccount({ parameters: { allowRagLogging: newValue } });
                                            callback(newValue);
                                        });
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("RAG logging"),
                                    Paragraph("Record RAG searches, including queries, returned documents, scores, processing time, and cost, so you can optimize your collections."),
                                    Paragraph("Records are available only to your account in each collection's Transactions tab and query coverage metrics. They are not used for model training.")
                                )
                            ),
                            Editor(
                                {
                                    id: "allowSemanticDataCollection",
                                    value: session.data.parameters?.allowSemanticDataCollection ?? false,
                                    renderValue(value) {
                                        return CheckWrapper(value, "allowed", "not allowed");
                                    },
                                    manipulateValue(value, callback) {
                                        const newValue = !value;
                                        runOperation("Updating account...", async () => {
                                            await updateAccount({ parameters: { allowSemanticDataCollection: newValue } });
                                            callback(newValue);
                                        });
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Semantic data collection"),
                                    Paragraph("Allow AIVAX to collect RAG searches and Reflex requests and results for model training. Document indexing and storage are not collected."),
                                    Paragraph("Training records are stored without account, API key, request, collection, or document identifiers."),
                                    Paragraph("When enabled, RAG query embeddings and Reflex searches receive a 10% discount. Document indexing, storage, and RAG reranking remain at their regular prices.")
                                )
                            ),
                            Editor(
                                {
                                    id: "zeroDataRetentionOnly",
                                    value: session.data.parameters?.zeroDataRetentionOnly ?? false,
                                    renderValue(value) {
                                        return CheckWrapper(value, "enabled", "disabled");
                                    },
                                    manipulateValue(value, callback) {
                                        const newValue = !value;
                                        runOperation("Updating account...", async () => {
                                            await updateAccount({ parameters: { zeroDataRetentionOnly: newValue } });
                                            callback(newValue);
                                        });
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Zero data retention providers only"),
                                    Paragraph("Route text inference with integrated models only to providers that do not retain prompts. Providers that are not zero data retention are excluded."),
                                    Paragraph("Requests fail when the selected model has no zero data retention provider available. This setting does not change conversation logging.")
                                )
                            ),
                            Editor(
                                {
                                    id: "allowSpecialProviders",
                                    value: session.data.parameters?.allowSpecialProviders ?? false,
                                    renderValue(value) {
                                        return CheckWrapper(value, "allowed", "not allowed");
                                    },
                                    manipulateValue(value, callback) {
                                        const newValue = !value;
                                        runOperation("Updating account...", async () => {
                                            await updateAccount({ parameters: { allowSpecialProviders: newValue } });
                                            callback(newValue);
                                        });
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Allow special providers"),
                                    Paragraph("Allow text inference with integrated models to be routed to special providers, which serve models at discounted prices under conditions that may be less transparent than regular providers."),
                                    Paragraph("When enabled, special providers are considered by the Lowest cost, Fastest, and Highest quality routing preferences. Balanced never uses them, and the Special routing preference always includes and prioritizes them.")
                                )
                            )
                        )
                    )
                },
                {
                    title: "Notifications",
                    contents: el.fragment(
                        Heading(
                            "Notifications",
                            Paragraph("Choose where account notifications should be delivered and which events should trigger them.")
                        ),
                        Section(
                            Editor(
                                {
                                    id: "notificationEmails",
                                    readonly: true,
                                    hoverState: false,
                                    value: session.data.email,
                                    renderValue(value) {
                                        return MutedText(value || "Account e-mail");
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Notification e-mails"),
                                    Paragraph("Notifications are currently sent only to your account e-mail.")
                                )
                            )
                        ),
                        Heading(
                            "Low balance",
                            Paragraph("Send an e-mail when your account balance falls below the configured threshold.")
                        ),
                        Section(
                            Editor(
                                {
                                    id: "lowBalanceNotification",
                                    value: normalizeNotificationSettings(session.data.parameters).lowBalance.enabled,
                                    renderValue(value) {
                                        return CheckWrapper(value, "enabled", "disabled");
                                    },
                                    manipulateValue(value, callback) {
                                        const newValue = !value;
                                        runOperation("Updating notifications...", async () => {
                                            const notifications = normalizeNotificationSettings(session.data.parameters);
                                            notifications.lowBalance.enabled = newValue;
                                            await updateAccount({ parameters: { notifications } });
                                            callback(newValue);
                                        });
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Status"),
                                    Paragraph("Enable or disable low balance notifications for this account.")
                                )
                            ),
                            Editor(
                                {
                                    id: "lowBalanceNotificationThreshold",
                                    value: normalizeNotificationSettings(session.data.parameters).lowBalance.threshold,
                                    renderValue(value) {
                                        return Button(`$${Number(value || 20).toFixed(2)}`);
                                    },
                                    manipulateValue(value, callback) {
                                        displayFormControl({
                                            title: this.editorTitle,
                                            message: this.editorParagraphs,
                                            formControlItem: NumberInput({ value, min: 1, max: 300, step: 1 }),
                                            onConfirm(newThreshold) {
                                                runOperation("Updating notifications...", async () => {
                                                    const notifications = normalizeNotificationSettings(session.data.parameters);
                                                    notifications.lowBalance.threshold = newThreshold;
                                                    await updateAccount({ parameters: { notifications } });
                                                    callback(newThreshold);
                                                });
                                            }
                                        })
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Balance threshold"),
                                    Paragraph("Low balance notifications are sent at most once every 3 days.")
                                )
                            )
                        ),
                        Heading(
                            "Agentic tests",
                            Paragraph("Receive a weekly overview or an alert after three consecutive failures.")
                        ),
                        Section(
                            Editor(
                                {
                                    id: "weeklyTestingSummary",
                                    value: normalizeNotificationSettings(session.data.parameters).weeklyTestingSummary,
                                    renderValue(value) {
                                        return CheckWrapper(value, "enabled", "disabled");
                                    },
                                    manipulateValue(value, callback) {
                                        const newValue = !value;
                                        runOperation("Updating notifications...", async () => {
                                            const notifications = normalizeNotificationSettings(session.data.parameters);
                                            notifications.weeklyTestingSummary = newValue;
                                            await updateAccount({ parameters: { notifications } });
                                            callback(newValue);
                                        });
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Weekly testing summary"),
                                    Paragraph("Every 7 days, summarize the latest completed result of each test run during the period. Cancelled and pending runs are excluded; weeks without results are skipped.")
                                )
                            ),
                            Editor(
                                {
                                    id: "repeatedTestFailures",
                                    value: normalizeNotificationSettings(session.data.parameters).repeatedTestFailures,
                                    renderValue(value) {
                                        return CheckWrapper(value, "enabled", "disabled");
                                    },
                                    manipulateValue(value, callback) {
                                        const newValue = !value;
                                        runOperation("Updating notifications...", async () => {
                                            const notifications = normalizeNotificationSettings(session.data.parameters);
                                            notifications.repeatedTestFailures = newValue;
                                            await updateAccount({ parameters: { notifications } });
                                            callback(newValue);
                                        });
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Repeated failures"),
                                    Paragraph("Notify when a test reaches 3 consecutive failures, once per failure streak. A successful run resets the streak.")
                                )
                            )
                        ),
                        Heading(
                            "Deprecated models",
                            Paragraph("Send an e-mail when recently used models become deprecated.")
                        ),
                        Section(
                            Editor(
                                {
                                    id: "deprecatedModelsNotification",
                                    value: normalizeNotificationSettings(session.data.parameters).deprecatedModels.enabled,
                                    renderValue(value) {
                                        return CheckWrapper(value, "enabled", "disabled");
                                    },
                                    manipulateValue(value, callback) {
                                        const newValue = !value;
                                        runOperation("Updating notifications...", async () => {
                                            const notifications = normalizeNotificationSettings(session.data.parameters);
                                            notifications.deprecatedModels.enabled = newValue;
                                            await updateAccount({ parameters: { notifications } });
                                            callback(newValue);
                                        });
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Status"),
                                    Paragraph("Only models used in the last 30 days are considered. These notifications are sent at most once every 7 days.")
                                )
                            )
                        )
                    )
                },
                {
                    title: "API Keys",
                    contents: el.fragment(
                        Heading(
                            "API Keys",
                            Paragraph("Configure your API keys to communicate with AIVAX services.")
                        ),
                        el.state.listening([apiKeys], () =>
                            Content(
                                Section(
                                    Row(
                                        Button(Icon("add-line"),
                                            "Create a new key",
                                            {
                                                onClick() {
                                                    displayFormControl({
                                                        title: "New API key",
                                                        message: "To create a new API key, enter the data below.",
                                                        formControlItem: SpecializedControl({
                                                            fields: [
                                                                {
                                                                    name: "label",
                                                                    label: "Key name",
                                                                    control: TextInput(null, "My key")
                                                                },
                                                                {
                                                                    name: "type",
                                                                    label: "Key type",
                                                                    control: Select({
                                                                        value: "private",
                                                                        items: [
                                                                            {
                                                                                text: "Private",
                                                                                value: "private"
                                                                            },
                                                                            {
                                                                                text: "Public (beta)",
                                                                                value: "public"
                                                                            }
                                                                        ]
                                                                    })
                                                                },
                                                                {
                                                                    name: "duration",
                                                                    label: "Key expiration",
                                                                    control: Select({
                                                                        items: [
                                                                            {
                                                                                text: "7d",
                                                                                value: "7"
                                                                            },
                                                                            {
                                                                                text: "30d",
                                                                                value: "30"
                                                                            },
                                                                            {
                                                                                text: "90d",
                                                                                value: "90"
                                                                            },
                                                                            {
                                                                                text: "Never",
                                                                                value: "-1"
                                                                            }
                                                                        ]
                                                                    })
                                                                }
                                                            ]
                                                        }),
                                                        onConfirm: value => {
                                                            runOperation("Creating API key...", async () => {
                                                                const response = await api.postJson("/api/v1/accounts/api-key", value);
                                                                if (response.data && response.data.grantKey) {
                                                                    displayAlert({
                                                                        title: "Key created",
                                                                        message: el.fragment(
                                                                            Paragraph("Your API key was successfully generated."),
                                                                            Paragraph("See your key below:"),
                                                                            BlockCodeWrapper("Generated API Key", response.data.grantKey),
                                                                            Warning(
                                                                                Paragraph(
                                                                                    "Warning: copy your API key to a safe place. ",
                                                                                    "You will not be able to see your key again."
                                                                                )
                                                                            )
                                                                        ),
                                                                        buttons: [
                                                                            {
                                                                                label: "OK",
                                                                                onClick(event) {
                                                                                    event.target.closest("dialog").dismiss();
                                                                                    getApiKeys();
                                                                                }
                                                                            }
                                                                        ]
                                                                    })
                                                                }
                                                            });
                                                        }
                                                    })
                                                }
                                            }
                                        )
                                    )
                                ),
                                Section(
                                    Card({ classList: "bg-card" },
                                        CardHeader("API Key List"),
                                        CardContent(
                                            Table(
                                                TableHeader(
                                                    TableHeaderCell("Key"),
                                                    TableHeaderCell("Type"),
                                                    TableHeaderCell("Key name"),
                                                    TableHeaderCell("Expiration"),
                                                    TableHeaderCell("Last used"),
                                                    TableHeaderActionCell("Actions"),
                                                ),
                                                TableBody(
                                                    apiKeys.value?.apiKeys.map(k =>
                                                        TableRow(
                                                            TableCell(k.key),
                                                            TableCell(k.type === "public" ? "Public (beta)" : "Private"),
                                                            TableCell(k.label || MutedText("No name")),
                                                            TableCell(k.expiresAt && formatDateTime(k.expiresAt) || MutedText("No expiration")),
                                                            TableCell(k.usedAt && formatDateTime(k.usedAt) || MutedText("Never used")),
                                                            TableActionCell(
                                                                Dropdown(
                                                                    Icon("more-2-line"),
                                                                    DropdownItem("View conversation logs", Icon("dropdown-list"), {
                                                                        onClick() {
                                                                            page(`/dashboard/conversations?${new URLSearchParams({
                                                                                queryFilter: `--api-key ${k.id}`
                                                                            }).toString()}`);
                                                                        }
                                                                    }),
                                                                    DropdownItem("Delete", Icon("delete-bin-7-line"), {
                                                                        onClick() {
                                                                            showFlyout(
                                                                                Paragraph(
                                                                                    "Are you sure you want to delete this key?"
                                                                                ),
                                                                                Paragraph(
                                                                                    "All services that use this key will be unavailable."
                                                                                ),
                                                                                DangerButton(
                                                                                    "Yes, delete key",
                                                                                    {
                                                                                        onClick() {
                                                                                            runOperation("Deleting key...", async () => {
                                                                                                await api.deleteJson(`/api/v1/accounts/api-key/${k.id}`);
                                                                                                getApiKeys();
                                                                                            });
                                                                                        }
                                                                                    }
                                                                                )
                                                                            )
                                                                        }
                                                                    })
                                                                )
                                                            )
                                                        )
                                                    )?.nullWhenEmpty() || DefaultRow("You have not created any API keys.")
                                                )
                                            )
                                        )
                                    )
                                )
                            )
                        )
                    )
                },
                {
                    title: "Authorized applications",
                    contents: el.fragment(
                        Heading(
                            "Authorized applications",
                            Paragraph("Third-party applications you allowed to access your account. Revoking an application invalidates its access token immediately.")
                        ),
                        el.state.listening([applications], () =>
                            Content(
                                Card({ classList: "bg-card" },
                                    CardHeader("Application list"),
                                    CardContent(
                                        Table(
                                            TableHeader(
                                                TableHeaderCell("Application"),
                                                TableHeaderCell("Permissions"),
                                                TableHeaderCell("Authorized"),
                                                TableHeaderCell("Expiration"),
                                                TableHeaderCell("Last used"),
                                                TableHeaderActionCell("Actions"),
                                            ),
                                            TableBody(
                                                applications.value?.map(app =>
                                                    TableRow(
                                                        TableCell(app.name || MutedText("No name")),
                                                        TableCell([...app.permissions, "AccountIdentity"].join(", ")),
                                                        TableCell(formatDateTime(app.createdAt)),
                                                        TableCell(formatDateTime(app.expiresAt)),
                                                        TableCell(app.usedAt && formatDateTime(app.usedAt) || MutedText("Never used")),
                                                        TableActionCell(
                                                            Dropdown(
                                                                Icon("more-2-line"),
                                                                DropdownItem("Revoke access", Icon("forbid-line"), {
                                                                    onClick() {
                                                                        showFlyout(
                                                                            Paragraph(`Revoke access for ${app.name || "this application"}?`),
                                                                            Paragraph("The application will no longer be able to use your account."),
                                                                            DangerButton(
                                                                                "Yes, revoke access",
                                                                                {
                                                                                    onClick() {
                                                                                        runOperation("Revoking access...", async () => {
                                                                                            await api.deleteJson(`/api/v1/accounts/api-key/${app.id}`);
                                                                                            getApplications();
                                                                                        });
                                                                                    }
                                                                                }
                                                                            )
                                                                        )
                                                                    }
                                                                })
                                                            )
                                                        )
                                                    )
                                                )?.nullWhenEmpty() || DefaultRow("You have not authorized any applications.")
                                            )
                                        )
                                    )
                                )
                            )
                        )
                    )
                },
                {
                    title: "Hook key",
                    contents: el.fragment(
                        Heading(
                            "Hook key",
                            Paragraph("The hook key defines the secret word for AIVAX reverse calls (webhooks) to your services, such as function calls, workers, etc."),
                            Paragraph("Read more about AIVAX request authentication in the ", ExternalLink("documentation", "https://docs.aivax.net/docs/authentication#hook-authentication"), ".")
                        ),
                        el.state.listening([apiKeys], () =>
                            SmallContainer(
                                Card({ classList: "bg-card" },
                                    CardContent(
                                        Section(
                                            FormControl(
                                                "Hook key",
                                                TextInput(null, "not generated", { readonly: true, value: apiKeys.value?.salt && ellipsis(apiKeys.value?.salt, 12) })
                                            ),
                                            CommandBar(
                                                { style: { marginTop: ".8em" } },
                                                Button(
                                                    Icon("loop-right-line"),
                                                    "Reset hook key",
                                                    {
                                                        onClick() {
                                                            showFlyout(
                                                                Paragraph(
                                                                    "Are you sure you want to reset the hook key?"
                                                                ),
                                                                Paragraph(
                                                                    "You will need to update it in all services that use it."
                                                                ),
                                                                DangerButton(
                                                                    "Reset hook key",
                                                                    {
                                                                        onClick() {
                                                                            runOperation("Resetting hook key...", async () => {
                                                                                await api.postJson(`/api/v1/accounts/roll-salt`);
                                                                                getApiKeys();
                                                                            });
                                                                        }
                                                                    }
                                                                )
                                                            )
                                                        }
                                                    }
                                                ),
                                                Button(
                                                    Icon("file-copy-line"),
                                                    "Copy",
                                                    {
                                                        onClick() {
                                                            copyToClipboard(apiKeys.value?.salt)
                                                        }
                                                    }
                                                )
                                            )
                                        )
                                    )
                                )
                            )
                        )
                    )
                }
            ])
        )
    )
}
